import React from 'react';
import { Button, Card, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { startCase } from 'lodash';
import { Link } from 'react-router-dom';

import { tInsuranceTypeEnum } from '@models/InsuranceType';
import { InsurancePolicyItemList } from '@models/InsurancePolicyItem';
import { getInsurancePolicyStatusVariant, InsurancePolicyStatuses } from '@models/InsurancePolicy';
import { InsurerLogo } from '@components/Feature/Insurer';
import { InsuranceTypesEnum } from '@models/InsuranceType';
import { formatCurrency } from '@utils/currency';
import { dUserName } from '@models/User';
import { dDatePeriod } from '@utils/datetime';

import { RowItem } from './row-item';
import { routeUserDashboardInsurancePolicyClaim, routeUserDashboardInsurancePolicyUserDetailsPage } from '../route';

type Props = {
  policyItem: InsurancePolicyItemList;
};

const PolicyItemView = ({ policyItem }: Props) => {
  const { t } = useTranslation();
  const {
    id,
    insurancePolicyId,
    insuranceType,
    memberId,
    policyNumber,
    effectiveDate,
    expirationDate,
    ended,
    withinClaimSubmissionDeadline,
    insurer,
    policyBenefitTemplate,
    frontline,
    user,
  } = policyItem;
  const status = ended ? InsurancePolicyStatuses.Ended : InsurancePolicyStatuses.Active;
  const statusVariant = getInsurancePolicyStatusVariant(status);
  const claimProcedureLink =
    insuranceType === InsuranceTypesEnum.HLTH ? 'https://mgacover.com/en/articles/health/29' : '#';

  return (
    <Card border={statusVariant}>
      <Card.Header className="d-flex flex-row justify-content-between align-items-center bg-white">
        <h5 className="mb-0">{tInsuranceTypeEnum(insuranceType)}</h5>
        <InsurerLogo insurer={insurer} fluid />
      </Card.Header>
      <Card.Body>
        <RowItem label={t('common:memberName')} value={dUserName(user)} />
        <RowItem label={t('common:memberId')} value={memberId} />
        <RowItem label={t('common:policyNumber')} value={policyNumber} />
        <RowItem label={t('common:plan')} value={policyBenefitTemplate.name} />
        <RowItem label={t('common:periodOfInsurance')} value={dDatePeriod(effectiveDate, expirationDate)} />
        <RowItem label={t('digitalCard.annualLimit')} value={formatCurrency(policyBenefitTemplate.annualLimitAmount)} />
        <RowItem label={t('digitalCard.geographicCoverage')} value={policyBenefitTemplate.geographicCoverage} />

        <Stack gap={2}>
          <div>
            <small style={{ fontWeight: 'bold' }}>{t('digitalCard.forReimbursement')}: </small>
            <small>
              {frontline.name}, {t('common:telephone')}: {frontline.mobileNumber}
            </small>
          </div>
          <div>
            <small style={{ fontWeight: 'bold' }}>{t('digitalCard.forDirectBilling')}: </small>
            <small>
              {t('common:telephone')}: {insurer.telNumber}
            </small>
          </div>
          <div>
            <a href={claimProcedureLink} target="_blank" rel="noreferrer">
              <small style={{ fontWeight: 'bold' }}>{t('common:insuranceClaimProcedure')}</small>
            </a>
          </div>
        </Stack>

        <Stack direction="horizontal" gap={2} className="mt-3">
          <Button
            as={Link}
            to={routeUserDashboardInsurancePolicyUserDetailsPage({ insurancePolicyUserId: id })}
            variant="outline-primary"
          >
            {startCase(t('common:details'))}
          </Button>

          {(!ended || withinClaimSubmissionDeadline) && (
            <Button
              as={Link}
              to={routeUserDashboardInsurancePolicyClaim({ insurancePolicyId, insurancePolicyUserId: id })}
              variant="outline-primary"
            >
              {t('common:claim')}
            </Button>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
};

export { PolicyItemView };
